import { AlignmentType, Paragraph, TextRun } from 'docx';

// Utils
import {
  hasAnyValidValue,
  isEmptyValueArray,
  renderDateStringToMonthYear,
} from '@shared/utils';
import { parseRichText } from '@/components/template/resume/utils/richTextParser';
import {
  DEFAULT_RUN_CONFIG,
  bodyRun,
  buildIconPrefix,
  contentSectionTitle,
  dividerParagraph,
} from '../../utils';

// Types
import { TemplateProps } from '@/components/template/resume/type';

// Constants
import {
  FONT_FAMILY,
  FONT_SIZES,
  COLORS,
  LINE_SPACING,
  SPACINGS,
} from '../../constants';

// Tokens
import { SECTION_TITLES } from '@/components/template/resume/Default/tokens';

type Journey = NonNullable<TemplateProps['data']['journeys']>[number];
type Project = NonNullable<Journey['projects']>[number];

/** Creates tag chip runs for a tech stack list */
const createTagRuns = (items: string[]): TextRun[] =>
  items.filter(Boolean).flatMap((item, idx) => {
    const spacer =
      idx > 0
        ? [
            new TextRun({
              text: ' ',
              font: FONT_FAMILY,
              size: FONT_SIZES.xs,
            }),
          ]
        : [];
    return [
      ...spacer,
      new TextRun({
        text: ` ${item} `,
        font: FONT_FAMILY,
        size: FONT_SIZES.xs,
        shading: { fill: COLORS.surface },
      }),
    ];
  });

/** Creates paragraphs for a single project block */
const createProjectParagraphs = (
  project: Project,
  index: number,
): Paragraph[] => {
  if (!project || !hasAnyValidValue(project)) return [];

  const {
    start: pStart = '',
    end: pEnd = '',
    desc = '',
    role = '',
    stacks = [],
    name: projectName = '',
    duties = '',
  } = project;

  const paragraphs: Paragraph[] = [dividerParagraph()];

  if (projectName) {
    paragraphs.push(
      new Paragraph({
        children: [
          bodyRun(`Project #${index + 1} - ${projectName}`, {
            size: FONT_SIZES.xl,
            bold: true,
            allCaps: true,
          }),
        ],
      }),
    );
  }

  const dateRange = renderDateStringToMonthYear(pStart, pEnd);
  if (dateRange) {
    paragraphs.push(
      new Paragraph({
        children: [bodyRun(dateRange, { color: COLORS.primary })],
      }),
    );
  }

  if (desc) {
    for (const { runs } of parseRichText(desc, DEFAULT_RUN_CONFIG)) {
      paragraphs.push(
        new Paragraph({
          spacing: { line: LINE_SPACING },
          alignment: AlignmentType.JUSTIFIED,
          children: runs,
        }),
      );
    }
  }

  if (role) {
    paragraphs.push(
      new Paragraph({
        spacing: { before: SPACINGS.micro },
        children: [bodyRun('Role: ', { bold: true }), bodyRun(role)],
      }),
    );
  }

  for (const tech of stacks) {
    const { name: stackName = '', items: stackItems = [] } = tech;

    if (stackName) {
      paragraphs.push(
        new Paragraph({
          spacing: { before: SPACINGS.large, after: SPACINGS.small },
          children: [bodyRun(stackName, { bold: true })],
        }),
      );
    }

    if (stackItems.length) {
      // Render each item as a shaded chip
      paragraphs.push(
        new Paragraph({
          spacing: {
            before: SPACINGS.micro,
            after: SPACINGS.small,
            line: LINE_SPACING,
          },
          children: createTagRuns(stackItems),
        }),
      );
    }
  }

  if (duties) {
    paragraphs.push(
      new Paragraph({
        spacing: { before: SPACINGS.micro },
        children: [bodyRun('Responsibilities:', { bold: true })],
      }),
    );

    for (const { runs } of parseRichText(duties, DEFAULT_RUN_CONFIG)) {
      paragraphs.push(
        new Paragraph({ spacing: { line: LINE_SPACING }, children: runs }),
      );
    }
  }

  return paragraphs;
};

/** Creates the experience content section from the journeys array */
export const ExperiencesSection = (
  journeys: TemplateProps['data']['journeys'],
  locationIcon: Uint8Array | null,
): Paragraph[] => {
  if (!journeys || isEmptyValueArray(journeys)) return [];

  const paragraphs: Paragraph[] = [
    contentSectionTitle(SECTION_TITLES.experience),
  ];

  for (const [i, experience] of journeys.entries()) {
    const {
      company = '',
      address = '',
      start = '',
      end = '',
      title: jobTitle = '',
      projects = [],
    } = experience ?? {};

    if (i > 0) paragraphs.push(dividerParagraph());

    if (company) {
      paragraphs.push(
        new Paragraph({
          children: [
            bodyRun(company, {
              size: FONT_SIZES['3xl'],
              bold: true,
              color: COLORS.primary,
            }),
          ],
        }),
      );
    }

    if (address) {
      paragraphs.push(
        new Paragraph({
          spacing: { before: SPACINGS.micro, after: SPACINGS.micro },
          children: [...buildIconPrefix(locationIcon), bodyRun(address)],
        }),
      );
    }

    const dateRange = renderDateStringToMonthYear(start || '', end || '');
    if (dateRange) {
      paragraphs.push(
        new Paragraph({
          children: [bodyRun(dateRange, { color: COLORS.primary })],
        }),
      );
    }

    if (jobTitle) {
      paragraphs.push(
        new Paragraph({
          spacing: { before: SPACINGS.micro, after: SPACINGS.roleAfter },
          children: [bodyRun('Role: ', { bold: true }), bodyRun(jobTitle)],
        }),
      );
    }

    for (const [pi, project] of (projects ?? []).entries()) {
      paragraphs.push(...createProjectParagraphs(project, pi));
    }
  }

  return paragraphs;
};
